import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Share2, Users, GitBranch, ListTodo, Clock, Activity, ArrowRight, AlertCircle, Mic, Pencil, Search, FolderOpen, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loading, EmptyState, Panel, Btn } from "@/components/common";
import VoiceContactModal from "@/components/VoiceContactModal";
import { CreateEntityModal } from "@/pages/Entities";

const STAT_CARDS = [
  { key: "origins", label: "Active Networks", icon: Share2, to: "/app/spider-contact" },
  { key: "entities", label: "Entity Files", icon: Users, to: "/app/entities" },
  { key: "relationships", label: "Relationships", icon: GitBranch, to: "/app/entities" },
  { key: "candidates", label: "Candidates", icon: AlertCircle, to: "/app/review" },
  { key: "open_tasks", label: "Open Actions", icon: ListTodo, to: "/app/actions" },
  { key: "due_tasks", label: "Due / Overdue", icon: Clock, to: "/app/actions" },
];

// Human-readable labels for raw audit action codes shown on the dashboard.
// Technical codes/identifiers remain in the full history (Settings / audit).
const ACTION_LABELS = {
  "entity.create": "Contact added", "entity.update": "Contact edited",
  "entity.delete": "Contact deleted", "entity.archive": "Contact archived",
  "entity.restore": "Contact restored", "entity.merge": "Contacts merged",
  "note.create": "Note added", "note.update": "Note edited", "note.delete": "Note deleted",
  "personality.add": "Observation added", "importance.set": "Importance updated",
  "relationship.create": "Relationship added", "relationship.update": "Relationship edited",
  "field.add": "Field added", "field.update": "Field edited",
  "group.create": "Group created", "group.update": "Group renamed", "group.delete": "Group deleted",
  "group.member.add": "Added to group", "group.member.remove": "Removed from group",
  "origin.create": "Spider network created", "origin.update": "Spider network edited",
  "origin.archive": "Spider network archived", "membership.create": "Added to Spider network",
  "strategic_interest.create": "Strategic interest added",
  "report.workspace": "Workspace report generated", "report.group": "Group roster generated",
  "report.contact": "Contact report generated", "report.web": "Contact web report generated",
  "report.origin": "Spider report generated", "report.summary": "Summary report generated",
  "email.send": "Email sent", "email.draft": "Email draft saved",
  "email.draft.delete": "Email draft deleted", "email.smtp.connect": "Email mailbox connected",
  "backup.create": "Backup created", "backup.restore": "Backup restored",
};

function readableAction(a) {
  const base = ACTION_LABELS[a.action] || a.action.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const name = a.detail?.name || a.detail?.to || a.detail?.title || a.detail?.from;
  return name ? `${base} — ${name}` : base;
}

export default function Overview() {
  const { workspaceId, currentWorkspace, user, canEdit } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const [{ data }, att] = await Promise.all([
      api.get(`/overview?workspace_id=${workspaceId}`),
      api.get(`/overview/attention?workspace_id=${workspaceId}`).catch(() => ({ data: null })),
    ]);
    setData({ ...data, attention: att.data });
  }, [workspaceId]);

  useEffect(() => { setData(null); load(); }, [load]);

  if (!data) return <Loading />;
  const { stats, recent_activity, networks, attention } = data;
  const isSample = currentWorkspace?.is_sample || /sample/i.test(currentWorkspace?.name || "");

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="si-label mb-2">{currentWorkspace?.name}{isSample && <span className="ml-2" style={{ color: "var(--si-warning)" }}>· SAMPLE</span>}</div>
        <h1 className="text-2xl font-medium" style={{ color: "var(--si-text)" }}>Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
      </div>

      {isSample && (
        <div className="mb-6 flex items-start gap-2 px-4 py-3 text-sm" data-testid="sample-banner"
          style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderLeft: "3px solid var(--si-warning)", borderRadius: 2, color: "var(--si-text-2)" }}>
          <Info size={15} style={{ color: "var(--si-warning)", flexShrink: 0, marginTop: 2 }} />
          <span>This is the <strong style={{ color: "var(--si-text)" }}>sample workspace</strong> — names like Riverton Village Office and Margaret Hale are examples, not your records. Create a clean workspace for your real work in <Link to="/app/settings" className="underline">Settings</Link>.</span>
        </div>
      )}

      {/* Primary actions */}
      {canEdit && (
        <div className="mb-8 flex flex-wrap gap-2" data-testid="overview-actions">
          <Btn variant="primary" data-testid="overview-voice-btn" onClick={() => setVoiceOpen(true)}><Mic size={16} /> Add by Voice</Btn>
          <Btn variant="primary" data-testid="overview-manual-btn" onClick={() => setManualOpen(true)}><Pencil size={16} /> Enter Manually</Btn>
          <Link to="/app/entities"><Btn variant="secondary" data-testid="overview-find-btn"><Search size={16} /> Find a Contact</Btn></Link>
          <Link to="/app/groups"><Btn variant="secondary" data-testid="overview-groups-btn"><FolderOpen size={16} /> Open Groups</Btn></Link>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6" style={{ border: "1px solid var(--si-border)" }}>
        {STAT_CARDS.map((c) => (
          <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`}
            className="p-5 transition-colors duration-200"
            style={{ background: "var(--si-surface)", borderRight: "1px solid var(--si-border)", borderBottom: "1px solid var(--si-border)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--si-surface-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--si-surface)")}>
            <c.icon size={16} strokeWidth={1.5} style={{ color: "var(--si-silver)" }} />
            <div className="text-3xl font-mono mt-3" style={{ color: c.key === "due_tasks" && stats[c.key] > 0 ? "var(--si-warning)" : "var(--si-text)" }}>{stats[c.key]}</div>
            <div className="si-label mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      {attention && (attention.important_contacts.length > 0 || attention.due_commitments.length > 0 || attention.overdue_reviews.length > 0 || (attention.recent_developments || []).length > 0) && (
        <Panel className="mt-8" data-testid="attention-panel">
          <div className="flex items-center gap-2 px-5 py-4 text-sm font-medium" style={{ borderBottom: "1px solid var(--si-border)", color: "var(--si-text)" }}><AlertCircle size={15} strokeWidth={1.5} /> Needs your attention</div>
          <div className="grid md:grid-cols-4 gap-0">
            <div className="p-5" style={{ borderRight: "1px solid var(--si-border)" }}>
              <div className="si-label mb-2">Overdue reviews</div>
              {attention.overdue_reviews.slice(0, 6).map((r) => (
                <Link key={r.id} to={`/app/entities/${r.id}`} className="block text-sm mb-1.5" style={{ color: "var(--si-warning)" }}>{r.name} <span className="si-label">review due</span></Link>
              ))}
              {attention.overdue_reviews.length === 0 && <div className="si-label">All clear</div>}
            </div>
            <div className="p-5" style={{ borderRight: "1px solid var(--si-border)" }}>
              <div className="si-label mb-2">Upcoming commitments</div>
              {attention.due_commitments.slice(0, 6).map((t) => (
                <Link key={t.id} to="/app/actions" className="block text-sm mb-1.5" style={{ color: "var(--si-text)" }}>{t.title || "Commitment"} <span className="si-label">· {t.due_date ? new Date(t.due_date).toLocaleDateString() : "no date"}</span></Link>
              ))}
              {attention.due_commitments.length === 0 && <div className="si-label">None due</div>}
            </div>
            <div className="p-5" style={{ borderRight: "1px solid var(--si-border)" }}>
              <div className="si-label mb-2">Contacts needing attention</div>
              {attention.important_contacts.slice(0, 6).map((c) => (
                <Link key={c.id} to={`/app/entities/${c.id}`} className="block text-sm mb-1.5" style={{ color: "var(--si-text)" }} data-testid={`attn-contact-${c.id}`}>{c.name} <span className="si-label">· {c.level}</span></Link>
              ))}
              {attention.important_contacts.length === 0 && <div className="si-label">None flagged</div>}
            </div>
            <div className="p-5">
              <div className="si-label mb-2">Recent notes</div>
              {(attention.recent_developments || []).slice(0, 6).map((n) => (
                <Link key={n.id} to={n.entity_id ? `/app/entities/${n.entity_id}` : "/app/entities"} className="block text-sm mb-1.5" style={{ color: "var(--si-text-2)" }}>{n.title || (n.category ? n.category.replace(/_/g, " ") : "Note")}{n.entity_name ? <span className="si-label"> · {n.entity_name}</span> : null}</Link>
              ))}
              {(attention.recent_developments || []).length === 0 && <div className="si-label">Nothing new</div>}
            </div>
          </div>
        </Panel>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Panel>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--si-border)" }}>
            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--si-text)" }}><Share2 size={15} strokeWidth={1.5} /> My Spider Networks</div>
            <Link to="/app/spider-contact"><Btn variant="ghost" className="!py-1 !px-2 text-xs">Open <ArrowRight size={13} /></Btn></Link>
          </div>
          <div className="px-5 py-3 text-xs" style={{ borderBottom: "1px solid var(--si-border)", color: "var(--si-text-2)" }}>
            A Spider network starts from a place, person, organization, or digital location. Open one to see its connected contacts.
          </div>
          {networks.length === 0 ? (
            <EmptyState icon={Share2} title="No networks yet" hint="Create a Spider Contact origin to begin." />
          ) : (
            <div>
              {networks.map((n) => (
                <Link key={n.id} to={`/app/spider-contact?origin=${n.id}`} data-testid={`overview-network-${n.id}`}
                  className="flex items-center justify-between px-5 py-3 transition-colors duration-200"
                  style={{ borderBottom: "1px solid var(--si-border)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--si-surface-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <div>
                    <div className="text-sm" style={{ color: "var(--si-text)" }}>{n.name}{n.is_sample && <span className="si-label ml-2" style={{ color: "var(--si-warning)" }}>sample</span>}</div>
                    <div className="si-label mt-0.5">Starts from: {n.type}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono text-sm" style={{ color: "var(--si-silver)" }}>{n.entity_count} contacts</div>
                    <span className="si-label flex items-center gap-1" style={{ color: "var(--si-teal-light)" }}>Open network <ArrowRight size={12} /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="px-5 py-3 text-xs" style={{ color: "var(--si-text-2)" }}>
            <strong style={{ color: "var(--si-text)" }}>Groups</strong> are different: they organize contacts you choose, such as Potential Clients or Wellsboro Contacts. A contact can belong to several groups. <Link to="/app/groups" className="underline">Open Groups</Link>
          </div>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 px-5 py-4 text-sm font-medium" style={{ borderBottom: "1px solid var(--si-border)", color: "var(--si-text)" }}><Activity size={15} strokeWidth={1.5} /> Recent Activity</div>
          {recent_activity.length === 0 ? (
            <EmptyState icon={Activity} title="No activity yet" />
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              {recent_activity.map((a) => (
                <div key={a.id} className="px-5 py-3 text-sm" style={{ borderBottom: "1px solid var(--si-border)" }}>
                  <span style={{ color: "var(--si-text)" }}>{readableAction(a)}</span>
                  <div className="si-label mt-0.5">{a.user_email} · {new Date(a.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {voiceOpen && <VoiceContactModal workspaceId={workspaceId} onClose={() => setVoiceOpen(false)} onSaved={load} />}
      {manualOpen && <CreateEntityModal workspaceId={workspaceId} onClose={() => setManualOpen(false)} onCreated={(id) => navigate(`/app/entities/${id}`)} />}
    </div>
  );
}
